import type { UserRole } from "../api/types";

/**
 * What a role can COHERENTLY be shown — never what it is allowed to do.
 *
 * The authorization decision is server-side on every request (charter S-8);
 * nothing here is a control. What this module prevents is an incoherent UI:
 * a `billing_admin` holds `billing:view`/`billing:manage` and nothing else,
 * so rendering the fleet navigation for it would produce a shell full of
 * links that can only ever answer 403. Hiding them is a UX decision that
 * mirrors the server's grant table — kept in one place so it can be checked
 * against `server/internal/authz/model.go` in review, rather than scattered
 * across components as `role === "admin"` checks.
 */

/** Roles that hold operational grants on fleet resources (containers,
 * stacks, nodes, secrets) — i.e. everyone except the billing contact. */
export function canSeeFleet(role: string | undefined): boolean {
  return role === "admin" || role === "operator" || role === "viewer";
}

/** Roles the server grants `billing:view` to (admin, billing_admin). */
export function canSeeBilling(role: string | undefined): boolean {
  return role === "admin" || role === "billing_admin";
}

/** Where a freshly signed-in session should land. A billing contact has no
 * fleet view to land on, so it goes to the one page it can actually load. */
export function landingPathFor(role: string | undefined): string {
  return canSeeFleet(role) ? "/containers" : "/billing";
}

/** Human labels for the roles, shared by the account page and the admin
 * user list so they can never drift apart. */
export const ROLE_LABEL: Record<UserRole, string> = {
  admin: "Admin",
  operator: "Operator",
  viewer: "Viewer",
  billing_admin: "Billing admin",
};
